import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {
    IconButton,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Divider,
    Switch
} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {IconByKey} from '@oc/icons'
import {messages} from '../translations'
import {OcAgGridBaseStateWithHelpers, OcAgGridBasicLocalStorageItem} from './OcAgGridBaseStateWithHelpers'

interface OcAgGridResetTableSettingsButtonProps<
    T2 extends OcAgGridBasicLocalStorageItem,
    T1 extends OcAgGridBaseStateWithHelpers<T2>
> {
    /**
     * State class that extends from OcAgGridBaseStateWithHelpers
     */
    state: T1
    /**
     * Tooltip for the button - optional
     */
    toolTip?: string
    /**
     * ClassName for the icon - optional
     */
    iconClassName?: string
    /**
     * ClassName for the IconButton - optional
     */
    buttonClassName?: string
    /**
     * Icon key for selected icon from our @oc/icons - optional
     */
    iconKey?: string
}

interface OcAgGridManageVisibleColumnsButtonProps<
    T2 extends OcAgGridBasicLocalStorageItem,
    T1 extends OcAgGridBaseStateWithHelpers<T2>
> extends OcAgGridResetTableSettingsButtonProps<T2, T1> {
    /**
     * Object with extra mapping for column labels. The key is the colId from ColDefs, the value is label that should be displayed in the menu.
     * Can be used when some columns does not have headerName or simply different string needs to be displayed.
     * Optional.
     */
    labelMap?: {
        [key: string]: string
    }
    /**
     * If set to True, switch for enabling/disabling column autosizing will be displayed
     */
    showAutoSizeSwitch: boolean
}

const useStyles = makeOcStyles(theme => ({
    menu: {
        padding: theme.spacing(1, 0)
    },
    menuItem: {
        padding: theme.spacing(0, 3)
    },
    menuTitle: {
        paddingLeft: theme.spacing(3),
        paddingBottom: theme.spacing(2),
        fontWeight: theme.typography.fontWeightBold?.valueOf() as number
    }
}))

function _OcAgGridResetTableSettingsButton<
    T2 extends OcAgGridBasicLocalStorageItem,
    T1 extends OcAgGridBaseStateWithHelpers<T2>
>(props: OcAgGridResetTableSettingsButtonProps<T2, T1>) {
    const {state, iconClassName, iconKey, toolTip, buttonClassName} = props

    return (
        <Tooltip title={toolTip || messages.getText('agGrid', 'resetDefault')}>
            <IconButton
                data-cy="oc-ag-grid-reset-table-settings-button"
                onClick={state.restoreDefaultTableSettings}
                className={buttonClassName}
            >
                <IconByKey iconKey={iconKey || 'backToStart'} className={iconClassName} />
            </IconButton>
        </Tooltip>
    )
}

/**
 * Button for reseting ag grid to default after user made some layout changes (hide columns, resize,...)
 * It needs state class that extends from OcAgGridBaseStateWithHelpers
 */
export const OcAgGridResetTableSettingsButton = observer(_OcAgGridResetTableSettingsButton)

function _OcAgGridManageVisibleColumnsButton<
    T2 extends OcAgGridBasicLocalStorageItem,
    T1 extends OcAgGridBaseStateWithHelpers<T2>
>(props: OcAgGridManageVisibleColumnsButtonProps<T2, T1>) {
    const {state, iconClassName, iconKey, toolTip, buttonClassName, labelMap, showAutoSizeSwitch} = props
    const {classes} = useStyles()

    const [exportAnchor, setExportAnchor] = React.useState<HTMLElement | null>(null)
    const closeSelectedColumnsMenu = () => setExportAnchor(null)
    const openSelectedColumnsMenu = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setExportAnchor(event.target as HTMLButtonElement)
    }
    return (
        <div>
            <Tooltip title={toolTip || messages.getText('agGrid', 'showColumnMenu')}>
                <IconButton
                    data-cy="open-visible-columns-menu-button"
                    onClick={openSelectedColumnsMenu}
                    className={buttonClassName}
                >
                    <IconByKey iconKey={iconKey || 'watchdog_tabs_watches'} className={iconClassName} />
                </IconButton>
            </Tooltip>
            <Menu
                open={!!exportAnchor}
                onClose={closeSelectedColumnsMenu}
                anchorEl={exportAnchor}
                classes={{list: classes.menu}}
            >
                <Typography variant="h4" className={classes.menuTitle}>
                    {messages.getText('agGrid', 'displayedColumns')}
                </Typography>
                {Array.from(state.columnMenuOptions).map(columnOption => {
                    const option = columnOption as {
                        columnId: string
                        hide: boolean
                        label: string
                    }
                    return (
                        <MenuItem
                            key={option.columnId}
                            data-cy={`show-hide-column-${option.columnId}-checkbox`}
                            onClick={() => state.changeColumnVisibility(option.columnId, !option.hide)}
                            className={classes.menuItem}
                        >
                            <ListItemIcon>
                                <Checkbox checked={!option.hide} color="primary" />
                            </ListItemIcon>
                            <ListItemText>{labelMap?.[option.columnId] || option.label}</ListItemText>
                        </MenuItem>
                    )
                })}

                {showAutoSizeSwitch ? (
                    <>
                        <Divider />
                        <MenuItem
                            data-cy="disable-column-auto-sizing-switch"
                            onClick={() => state.toggleDisableColumnAutoSize()}
                            className={classes.menuItem}
                        >
                            <ListItemIcon>
                                <Switch checked={!state.disableColumnAutoSize} color="primary" />
                            </ListItemIcon>
                            <ListItemText>{messages.getText('agGrid', 'autoResize')}</ListItemText>
                        </MenuItem>
                    </>
                ) : undefined}
            </Menu>
        </div>
    )
}

/**
 * Button and menu for settings visible columns and enabling/disabling auto column resize.
 * Menu is automatically generated, but it is possible to pass 'labelMap' to modify what text will be displayed for each column.
 * It needs state class that extends from OcAgGridBaseStateWithHelpers
 */
export const OcAgGridManageVisibleColumnsButton = observer(_OcAgGridManageVisibleColumnsButton)
